import type { ApiClient } from "@ncp/api-client";
import { sendsInParts, uploadInParts, type PartProgress } from "./attachmentParts";
import type {
  Artifact,
  ArtifactExportFormat,
  ArtifactFilter,
  AttachArtifactRequest,
  AttachArtifactResult,
  AssistantCatalog,
  AssistantStreamEvent,
  AssistantSummary,
  AttachmentUploadResult,
  CreateThreadRequest,
  DecideConfirmationResponse,
  DeepLinks,
  DeepLinkTarget,
  EditArtifactRequest,
  Home,
  HomeRun,
  InboxFilter,
  InboxPage,
  InboxSummary,
  KnowledgeChunk,
  KnowledgeCollection,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  KnowledgeUploadResult,
  PatchThreadRequest,
  SendMessageRequest,
  SidebarThreadResponse,
  AgentRoster,
  SpaceApp,
  SpaceAppDetail,
  Thread,
  ThreadDetail,
  ThreadFilter,
  ThreadHandoffResponse,
  ThreadMessage,
  ToolDescriptor,
} from "./types";

export const ASSISTANT_API = "/api/assistant";

export interface KnowledgeUploadMeta {
  title?: string;
  titleAr?: string;
  kind?: string;
  issuer?: string;
  /** ISO date (yyyy-MM-dd). */
  issuedOn?: string;
  sourceUrl?: string;
}

/**
 * Typed access to the Surface API. One instance per `ApiClient`; every surface (parent app, embedded case panel, future channels)
 * goes through this class so the contract lives in one place.
 */
export class AssistantApi {
  constructor(private readonly api: ApiClient) {}

  // ---- shell
  catalog(): Promise<AssistantCatalog> {
    return this.api.get<AssistantCatalog>(`${ASSISTANT_API}/catalog`);
  }

  home(workspaceKey?: string | null): Promise<Home> {
    return this.api.get<Home>(`${ASSISTANT_API}/home`, { workspace: workspaceKey ?? undefined });
  }

  assistants(workspaceKey: string): Promise<AssistantSummary[]> {
    return this.api.get<AssistantSummary[]>(`${ASSISTANT_API}/assistants`, { workspace: workspaceKey });
  }

  tools(): Promise<ToolDescriptor[]> {
    return this.api.get<ToolDescriptor[]>(`${ASSISTANT_API}/tools`);
  }

  // ---- apps (the pages a space puts in its navigation, as the launcher and the About view draw them)
  /** The space's apps in navigation order, with their live figures read for the caller (members only; 404 otherwise). */
  spaceApps(key: string, locale?: string): Promise<SpaceApp[]> {
    return this.api.get<SpaceApp[]>(`${ASSISTANT_API}/workspaces/${encodeURIComponent(key)}/apps`, { locale });
  }

  /** One app's About view: where the space shows it, its agents, jobs and data, and the other spaces it is in. */
  spaceApp(key: string, page: string, locale?: string): Promise<SpaceAppDetail> {
    return this.api.get<SpaceAppDetail>(`${ASSISTANT_API}/workspaces/${encodeURIComponent(key)}/apps/${encodeURIComponent(page)}`, { locale });
  }

  // ---- threads
  threads(filter: ThreadFilter = {}): Promise<Thread[]> {
    return this.api.get<Thread[]>(`${ASSISTANT_API}/threads`, { ...filter });
  }

  createThread(input: CreateThreadRequest): Promise<Thread> {
    return this.api.post<Thread>(`${ASSISTANT_API}/threads`, input);
  }

  /** The caller's conversation about a case from a host surface (registry key), created in the case's workspace when there is none. */
  caseThread(caseId: string, surfaceKey: string, opts: { locale?: string; fresh?: boolean } = {}): Promise<Thread> {
    return this.api.post<Thread>(`${ASSISTANT_API}/threads/case/${caseId}`, { surfaceKey, locale: opts.locale, fresh: opts.fresh ?? false });
  }

  // ---- registry deep links
  links(target: DeepLinkTarget): Promise<DeepLinks> {
    const { scope, ...ids } = target;
    return this.api.get<DeepLinks>(`${ASSISTANT_API}/links`, { scope, ...Object.fromEntries(Object.entries(ids).filter(([, v]) => !!v)) });
  }

  thread(id: string): Promise<ThreadDetail> {
    return this.api.get<ThreadDetail>(`${ASSISTANT_API}/threads/${id}`);
  }

  patchThread(id: string, patch: PatchThreadRequest): Promise<Thread> {
    return this.api.patch<Thread>(`${ASSISTANT_API}/threads/${id}`, patch);
  }

  deleteThread(id: string): Promise<void> {
    return this.api.delete<void>(`${ASSISTANT_API}/threads/${id}`);
  }

  threadRuns(id: string): Promise<HomeRun[]> {
    return this.api.get<HomeRun[]>(`${ASSISTANT_API}/threads/${id}/runs`);
  }

  feedback(threadId: string, messageId: string, feedback: number | null, note?: string | null): Promise<ThreadMessage> {
    return this.api.post<ThreadMessage>(`${ASSISTANT_API}/threads/${threadId}/messages/${messageId}/feedback`, { feedback, note: note ?? null });
  }

  /** Keeps an assistant answer as a draft document in the conversation's drafts and files. */
  /** The conversation as a cited brief: a DRAFT artifact of the thread (a new version each time), exportable as Word or PDF. */
  conversationBrief(threadId: string, locale: "en" | "ar"): Promise<Artifact> {
    return this.api.post<Artifact>(`${ASSISTANT_API}/threads/${threadId}/brief?locale=${locale}`, {});
  }

  saveMessage(threadId: string, messageId: string): Promise<Artifact> {
    return this.api.post<Artifact>(`${ASSISTANT_API}/threads/${threadId}/messages/${messageId}/save`, {});
  }

  /**
   * Attaches files of up to 200 MB each: files up to 20 MB in one multipart request, larger ones in 5 MiB parts (`uploadInParts`), so no
   * proxy in front of the API carries a 200 MB body. The answers are merged as one.
   */
  async attach(threadId: string, files: File[], onProgress?: (p: PartProgress) => void): Promise<AttachmentUploadResult> {
    const small = files.filter((f) => !sendsInParts(f));
    const large = files.filter(sendsInParts);
    const results: AttachmentUploadResult[] = [];
    if (small.length > 0 || large.length === 0) {
      const form = new FormData();
      for (const f of small) form.append("file", f, f.name);
      results.push(await this.api.upload<AttachmentUploadResult>(`${ASSISTANT_API}/threads/${threadId}/attachments`, form));
    }
    for (const f of large) results.push(await uploadInParts(this.api, `${ASSISTANT_API}/threads/${threadId}/attachments/uploads`, f, onProgress));
    return results.length === 1 ? results[0]! : { attachments: results.flatMap((r) => r.attachments), errors: results.flatMap((r) => r.errors) };
  }

  /** Confirm or cancel a staged action; `edits` rewords the card's visible fields (title, summary, note…) before it runs. */
  decideConfirmation(threadId: string, confirmationId: string, accept: boolean, edits?: Record<string, unknown>): Promise<DecideConfirmationResponse> {
    return this.api.post<DecideConfirmationResponse>(`${ASSISTANT_API}/threads/${threadId}/confirmations/${confirmationId}`, { accept, arguments: edits ?? null });
  }

  // ---- the ECONIC Agent sidebar (builder §17.4–§17.8)
  /** The department's follow-me conversation (surface `econic:<space>`): the latest active one, or a new one; `fresh` starts another. */
  sidebarThread(space: string, opts: { locale?: string | null; fresh?: boolean } = {}): Promise<SidebarThreadResponse> {
    return this.api.post<SidebarThreadResponse>(`${ASSISTANT_API}/sidebar/thread`, { space, locale: opts.locale ?? null, fresh: opts.fresh ?? false });
  }

  // ---- the ECONIC Agent screen (WP-SUPER-AGENT)
  /** A new conversation with the ECONIC Agent (a fresh follow-me thread of the department) or directly with one of its specialists. */
  agentThread(space: string, opts: { locale?: string | null; agent?: string | null } = {}): Promise<SidebarThreadResponse> {
    return this.api.post<SidebarThreadResponse>(`${ASSISTANT_API}/agent/thread`, { space, locale: opts.locale ?? null, agent: opts.agent ?? null });
  }

  /** The person's ECONIC Agent conversations in every department (the specialists' too), newest first. */
  agentThreads(q?: string | null, take = 40): Promise<Thread[]> {
    return this.api.get<Thread[]>(`${ASSISTANT_API}/agent/threads`, { ...(q ? { q } : {}), take });
  }

  /** The specialists the ECONIC Agent screen can talk to, and the desks' own agents. */
  agentSpecialists(): Promise<AgentRoster> {
    return this.api.get<AgentRoster>(`${ASSISTANT_API}/agent/specialists`);
  }

  /** "Bring this conversation": a summary in the target department's thread and a divider in this one (membership of both is checked). */
  handoff(threadId: string, toSpace: string): Promise<ThreadHandoffResponse> {
    return this.api.post<ThreadHandoffResponse>(`${ASSISTANT_API}/threads/${threadId}/handoff`, { toSpace });
  }

  /** Answers a `client_call` of a turn (host tools and page tools) within its 20 s window. */
  postClientResult(callId: string, result?: unknown, error?: string | null): Promise<void> {
    return this.api.post<void>("/api/pages/rpc/client-result", { callId, result: result ?? null, error: error ?? null });
  }

  /** Undoes the person's last change to a surface ("Applied for you · Undo"); returns the layout change. */
  undoLayout(surface: string, space?: string | null): Promise<unknown> {
    return this.api.post<unknown>(`${ASSISTANT_API}/layouts/${encodeURIComponent(surface)}/me/undo`, { space: space ?? null });
  }

  /** Applies ops to the person's own layer of a surface (Pin to Today: `widget.add` with `via: "pin"`). */
  patchMyLayout(surface: string, body: { space?: string | null; ops: unknown[]; via: "drag" | "menu" | "chat" | "settings" | "pin"; note?: string | null; threadId?: string | null; messageId?: string | null }): Promise<unknown> {
    return this.api.put<unknown>(`${ASSISTANT_API}/layouts/${encodeURIComponent(surface)}/me`, body);
  }

  /** Streams one turn; yields the SSE events in order and ends after `final` or `error`. */
  send(threadId: string, request: SendMessageRequest, signal?: AbortSignal): AsyncGenerator<AssistantStreamEvent, void, void> {
    return this.api.streamEvents<AssistantStreamEvent>(`${ASSISTANT_API}/threads/${threadId}/messages`, request, signal);
  }

  // ---- artifacts
  artifacts(filter: ArtifactFilter = {}): Promise<Artifact[]> {
    return this.api.get<Artifact[]>(`${ASSISTANT_API}/artifacts`, { ...filter });
  }

  artifact(id: string): Promise<Artifact> {
    return this.api.get<Artifact>(`${ASSISTANT_API}/artifacts/${id}`);
  }

  artifactVersions(id: string): Promise<Artifact[]> {
    return this.api.get<Artifact[]>(`${ASSISTANT_API}/artifacts/${id}/versions`);
  }

  editArtifact(id: string, edit: EditArtifactRequest): Promise<Artifact> {
    return this.api.put<Artifact>(`${ASSISTANT_API}/artifacts/${id}`, edit);
  }

  deleteArtifact(id: string): Promise<void> {
    return this.api.delete<void>(`${ASSISTANT_API}/artifacts/${id}`);
  }

  /** Renders the artifact on the server (Word/PDF on the letterhead, Excel, CSV…) and saves the file. */
  exportArtifact(id: string, format: ArtifactExportFormat, locale?: "en" | "ar"): Promise<void> {
    const q = new URLSearchParams({ format, ...(locale ? { locale } : {}) });
    return this.api.download(`${ASSISTANT_API}/artifacts/${id}/export?${q.toString()}`);
  }

  /** Files the rendered artifact on a matter as an internal document. */
  attachArtifact(id: string, request: AttachArtifactRequest): Promise<AttachArtifactResult> {
    return this.api.post<AttachArtifactResult>(`${ASSISTANT_API}/artifacts/${id}/attach`, request);
  }

  // ---- inbox
  inbox(filter: InboxFilter = {}): Promise<InboxPage> {
    return this.api.get<InboxPage>(`${ASSISTANT_API}/inbox`, { ...filter });
  }

  inboxSummary(workspaceKey?: string | null): Promise<InboxSummary> {
    return this.api.get<InboxSummary>(`${ASSISTANT_API}/inbox/summary`, { workspace: workspaceKey ?? undefined });
  }

  // ---- knowledge
  collections(workspaceKey?: string | null): Promise<KnowledgeCollection[]> {
    return this.api.get<KnowledgeCollection[]>(`${ASSISTANT_API}/knowledge/collections`, { workspace: workspaceKey ?? undefined });
  }

  documents(collectionKey: string, opts: { q?: string; kind?: string; includeInactive?: boolean; take?: number } = {}): Promise<KnowledgeDocument[]> {
    return this.api.get<KnowledgeDocument[]>(`${ASSISTANT_API}/knowledge/collections/${encodeURIComponent(collectionKey)}/documents`, { ...opts });
  }

  document(id: string, withChunks = true): Promise<KnowledgeDocumentDetail> {
    return this.api.get<KnowledgeDocumentDetail>(`${ASSISTANT_API}/knowledge/documents/${id}`, { withChunks });
  }

  chunk(id: string): Promise<{ chunk: KnowledgeChunk; document: KnowledgeDocument }> {
    return this.api.get<{ chunk: KnowledgeChunk; document: KnowledgeDocument }>(`${ASSISTANT_API}/knowledge/chunks/${id}`);
  }

  uploadDocument(collectionKey: string, file: File, meta: KnowledgeUploadMeta = {}): Promise<KnowledgeUploadResult> {
    const form = new FormData();
    form.append("file", file, file.name);
    for (const [k, v] of Object.entries(meta)) if (v) form.append(k, v);
    return this.api.upload<KnowledgeUploadResult>(`${ASSISTANT_API}/knowledge/collections/${encodeURIComponent(collectionKey)}/documents`, form);
  }

  removeDocument(id: string): Promise<void> {
    return this.api.delete<void>(`${ASSISTANT_API}/knowledge/documents/${id}`);
  }
}
